/** Food master validation rules (mirrors transport master pattern). */
import type { MasterFieldDef } from './masters/types';
import { slugifyMasterCode } from './onDemandMasters';
import {
  TRANSPORT_DAY_OF_WEEK_OPTIONS,
  parseCommaSeparatedDayValues,
} from './transportSubserviceMasters';

/** Food master codes — lowercase snake_case (e.g. extra_cheese, french_fries). */
export function slugifyFoodMasterCode(name: string): string {
  return slugifyMasterCode(name).toLowerCase();
}

export function isValidFoodMasterCode(code: string): boolean {
  return /^[a-z0-9_]+$/.test(code.trim());
}

/** Food master types that auto-generate code from display name (create flow). */
export const FOOD_AUTO_CODE_TYPES = new Set<string>([
  'restaurant_types',
  'restaurant_branches',
  'restaurant_opening_hours',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'food_variants',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food master types that hide the record-level Active toggle on the add/edit form. */
export const FOOD_HIDE_STATUS_TOGGLE_TYPES = new Set<string>([
  'restaurant_types',
  'restaurant_opening_hours',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'food_variants',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food master types whose list table shows active/inactive as a switch (no status chip text). */
export const FOOD_STATUS_TOGGLE_LIST_TYPES = new Set<string>([
  'restaurant_branches',
  'restaurant_opening_hours',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'food_variants',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food master types with description spam validation (repeated chars/words). */
export const FOOD_DESCRIPTION_VALIDATED_TYPES = new Set<string>([
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food master types whose display name allows letters and spaces only. */
export const FOOD_LETTER_ONLY_NAME_TYPES = new Set<string>([
  'cuisine_types',
]);

/** Food master types that show the category/image upload in Basic Info (always visible without scrolling). */
export const FOOD_IMAGE_IN_BASIC_INFO_TYPES = new Set<string>([
  'food_categories',
  'cuisine_types',
  'restaurant_types',
  'dietary_types',
  'food_addons',
]);

/** Food master types that use metadata.description only (no duplicate top-level Description field). */
export const FOOD_METADATA_DESCRIPTION_TYPES = new Set<string>([
  'food_sub_categories',
]);

/** Food master types whose parent category picker lives in Basic Info (not Parent Master + Properties). */
export const FOOD_PARENT_IN_BASIC_INFO_TYPES = new Set<string>([
  'food_sub_categories',
  'grocery_sub_categories',
]);

/** Food master types that hide the hierarchy parent picker (parent is chosen via metadata field). */
export const FOOD_HIDE_PARENT_PICKER_TYPES = new Set<string>([
  'food_categories',
  'food_sub_categories',
  'grocery_sub_categories',
  'food_attribute_values',
]);

/** Food master types whose list table shows Active/Inactive chip + toggle switch. */
export const FOOD_LIST_STATUS_TOGGLE_TYPES = new Set<string>([
  'restaurant_types',
]);

/** Food master types whose sort order must be a whole number ≥ 1 (no decimals or negatives). */
export const FOOD_INTEGER_SORT_ORDER_TYPES = new Set<string>([
  'restaurant_types',
  'restaurant_branches',
  'restaurant_opening_hours',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food master types whose sort order is locked to the next sequential value on create. */
export const FOOD_SEQUENTIAL_SORT_ORDER_TYPES = new Set<string>([
  'restaurant_types',
  'restaurant_branches',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'food_attribute_values',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Sort order unique within the same parent (category or attribute). */
export const FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'food_sub_categories',
  'food_attribute_values',
]);

/** Food master types whose sort order must be unique across records. */
export const FOOD_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'restaurant_types',
  'restaurant_branches',
  'restaurant_opening_hours',
  'food_categories',
  'cuisine_types',
  'dietary_types',
  'food_tags',
  'food_attributes',
  'addon_groups',
  'food_addons',
  'preparation_times',
  'food_units',
  'food_tax_groups',
  'kitchen_stations',
  'food_availability_schedules',
  'food_images',
]);

/** Food image master upload limits (enforced before upload). */
export const FOOD_IMAGE_MASTER_MAX_WIDTH = 1200;
export const FOOD_IMAGE_MASTER_MAX_HEIGHT = 800;

/** Standard GST-style tax group label — e.g. GST 5%, GST 12%. */
export function formatFoodTaxGroupName(taxPercent: number, prefix = 'GST'): string {
  const pct = Number(taxPercent);
  if (!Number.isFinite(pct)) return '';
  const display = Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, '');
  return `${prefix} ${display}%`;
}

export function formatTaxPercentDisplay(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const display = Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '');
  return `${display}%`;
}

export function resolveFoodTaxGroupDisplayName(row: {
  name?: string;
  metadata?: Record<string, unknown>;
}): string {
  const taxPercent = Number(row.metadata?.taxPercent);
  if (Number.isFinite(taxPercent)) {
    return formatFoodTaxGroupName(taxPercent);
  }
  return String(row.name ?? '').trim();
}

export function validateFoodTaxPercent(value: unknown): string | undefined {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Tax % must be a number';
  if (num < 0) return 'Tax % cannot be negative';
  if (num > 100) return 'Tax % cannot exceed 100';
  return undefined;
}

/** Tax group form: tax fields in Properties — sort order and status live elsewhere. */
export function enrichFoodTaxGroupMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => (
      field.key === 'taxPercent'
        ? {
          ...field,
          helperText: field.helperText || 'Enter a value from 0 to 100 — display name becomes e.g. GST 5%',
        }
        : field.key === 'taxIncluded'
          ? {
            ...field,
            helperText: field.helperText || 'Yes if tax is already included in the menu price',
          }
          : field
    ));
}

/** Common unit-type suggestions — users may also enter custom values (e.g. portion, serving). */
export const FOOD_UNIT_TYPE_SUGGESTIONS = [
  'count',
  'weight',
  'volume',
  'portion',
  'serving',
  'length',
] as const;

const FOOD_UNIT_TYPE_LABELS: Record<string, string> = {
  count: 'Count',
  weight: 'Weight',
  volume: 'Volume',
  portion: 'Portion',
  serving: 'Serving',
  length: 'Length',
};

export function normalizeFoodUnitType(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function formatFoodUnitTypeLabel(value: unknown): string {
  const code = normalizeFoodUnitType(value);
  if (!code) return '';
  if (FOOD_UNIT_TYPE_LABELS[code]) return FOOD_UNIT_TYPE_LABELS[code];
  return code.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function validateFoodUnitType(value: unknown): string | undefined {
  const unitType = normalizeFoodUnitType(value);
  if (!unitType) return 'Unit type is required';
  if (!/^[a-z][a-z0-9_]*$/.test(unitType)) {
    return 'Use lowercase letters, numbers, and underscores (e.g. volume, count)';
  }
  return undefined;
}

/** Common kitchen station type suggestions — custom values are also allowed. */
export const KITCHEN_STATION_TYPE_SUGGESTIONS = [
  'hot_kitchen',
  'cold_kitchen',
  'bakery',
  'grill',
  'beverage',
  'dessert',
  'salad',
  'prep',
] as const;

const KITCHEN_STATION_TYPE_LABELS: Record<string, string> = {
  hot_kitchen: 'Hot Kitchen',
  cold_kitchen: 'Cold Kitchen',
  bakery: 'Bakery',
  grill: 'Grill',
  beverage: 'Beverage',
  dessert: 'Dessert',
  salad: 'Salad',
  prep: 'Prep',
};

export function normalizeKitchenStationType(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function formatKitchenStationTypeLabel(value: unknown): string {
  const code = normalizeKitchenStationType(value);
  if (!code) return '';
  if (KITCHEN_STATION_TYPE_LABELS[code]) return KITCHEN_STATION_TYPE_LABELS[code];
  return code.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function validateKitchenStationType(value: unknown): string | undefined {
  const stationType = normalizeKitchenStationType(value);
  if (!stationType) return 'Station type is required';
  if (!/^[a-z][a-z0-9_]*$/.test(stationType)) {
    return 'Use lowercase letters, numbers, and underscores (e.g. hot_kitchen, bakery)';
  }
  return undefined;
}

/** Preparation time display name — always "{N} mins" (plural). */
export function formatPreparationTimeName(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return '';
  return `${minutes} mins`;
}

/** Preparation time code from minutes — e.g. prep_15. */
export function preparationTimeCodeFromMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return '';
  return `prep_${minutes}`;
}

export function resolvePreparationTimeMinutes(row: {
  metadata?: Record<string, unknown>;
}): number {
  const minutes = Number(row.metadata?.minutes);
  return Number.isFinite(minutes) && minutes >= 1 ? minutes : 0;
}

export function validateDuplicatePreparationMinutes(
  minutes: number,
  records: Array<{ _id?: string; metadata?: Record<string, unknown> }>,
  editingId?: string,
): string | undefined {
  if (!Number.isFinite(minutes) || minutes < 1) return undefined;
  const duplicate = records.some((row) => {
    if (editingId && row._id === editingId) return false;
    return resolvePreparationTimeMinutes(row) === minutes;
  });
  if (duplicate) {
    return `A preparation time of ${minutes} minutes already exists`;
  }
  return undefined;
}

export function validatePreparationTimeName(
  name: string,
  minutes: number,
): string | undefined {
  const expected = formatPreparationTimeName(minutes);
  if (!expected) return undefined;
  if (String(name ?? '').trim() !== expected) {
    return `Display name must be "${expected}" — set the Minutes field to update the name`;
  }
  return undefined;
}

export type FoodSubCategoryRecord = {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
};

export function resolveFoodSubCategoryParentCode(
  row: FoodSubCategoryRecord,
): string {
  return String(row.metadata?.parentCategoryCode ?? '').trim().toLowerCase();
}

export function resolveRecordSortOrder(row: FoodSubCategoryRecord): number {
  const top = row.sortOrder;
  const meta = Number(row.metadata?.sortOrder);
  const order = (typeof top === 'number' && Number.isFinite(top) && top >= 1) ? top : meta;
  return Number.isFinite(order) && order >= 1 ? order : 0;
}

/** Next unique sort order from existing food master rows (uses max + 1, not row count). */
export function getNextFoodMasterSortOrder(
  records: Array<{ sortOrder?: number; metadata?: Record<string, unknown> }>,
): number {
  const orders = records
    .map((row) => resolveRecordSortOrder(row))
    .filter((n) => n >= 1);
  if (!orders.length) return 1;
  return Math.max(...orders) + 1;
}

/** Next sort order among sibling sub-categories under the same parent. */
export function getNextFoodSubCategorySortOrder(
  records: FoodSubCategoryRecord[],
  parentCategoryCode: string,
): number {
  const parent = parentCategoryCode.trim().toLowerCase();
  if (!parent) return 1;
  const siblings = records.filter((row) => resolveFoodSubCategoryParentCode(row) === parent);
  const orders = siblings.map(resolveRecordSortOrder).filter((n) => n >= 1);
  if (!orders.length) return 1;
  return Math.max(...orders) + 1;
}

export function resolveFoodAttributeValueParentCode(
  row: FoodSubCategoryRecord & { attributeCode?: string },
): string {
  return String(row.metadata?.attributeCode ?? row.attributeCode ?? '').trim().toLowerCase();
}

/** Next sort order among values under the same parent food attribute. */
export function getNextFoodAttributeValueSortOrder(
  records: Array<FoodSubCategoryRecord & { attributeCode?: string }>,
  attributeCode: string,
): number {
  const parent = attributeCode.trim().toLowerCase();
  if (!parent) return 1;
  const siblings = records.filter((row) => resolveFoodAttributeValueParentCode(row) === parent);
  const orders = siblings.map(resolveRecordSortOrder).filter((n) => n >= 1);
  if (!orders.length) return 1;
  return Math.max(...orders) + 1;
}

export function buildFoodAttributeOrderMap(
  attributes: Array<{ code?: string; sortOrder?: number; metadata?: Record<string, unknown> }>,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of attributes) {
    const code = String(row.code ?? '').trim().toLowerCase();
    if (!code) continue;
    map.set(code, resolveRecordSortOrder(row));
  }
  return map;
}

export function sortFoodAttributeValueRows<T extends FoodSubCategoryRecord & { attributeCode?: string }>(
  rows: T[],
  attributeOrder?: Map<string, number>,
): T[] {
  return [...rows].sort((a, b) => {
    const codeA = resolveFoodAttributeValueParentCode(a);
    const codeB = resolveFoodAttributeValueParentCode(b);
    if (attributeOrder) {
      const orderA = attributeOrder.get(codeA) ?? 9999;
      const orderB = attributeOrder.get(codeB) ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
    } else if (codeA !== codeB) {
      return codeA.localeCompare(codeB);
    }
    const sortA = resolveRecordSortOrder(a);
    const sortB = resolveRecordSortOrder(b);
    if (sortA !== sortB) {
      if (sortA === 0) return 1;
      if (sortB === 0) return -1;
      return sortA - sortB;
    }
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

export function sortFoodMasterRowsBySortOrder<T extends FoodSubCategoryRecord>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

export function sortFoodSubCategoryRows<T extends FoodSubCategoryRecord>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const parentCmp = resolveFoodSubCategoryParentCode(a).localeCompare(resolveFoodSubCategoryParentCode(b));
    if (parentCmp !== 0) return parentCmp;
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    return a.name.localeCompare(b.name);
  });
}

export function validateDuplicateFoodSubCategoryName(
  name: string,
  parentCategoryCode: string,
  records: FoodSubCategoryRecord[],
  editingId?: string,
): string | undefined {
  const normalizedName = name.trim().toLowerCase();
  const parent = parentCategoryCode.trim().toLowerCase();
  if (!normalizedName || !parent) return undefined;
  const duplicate = records.some((row) => {
    if (editingId && row._id === editingId) return false;
    if (resolveFoodSubCategoryParentCode(row) !== parent) return false;
    return String(row.name ?? '').trim().toLowerCase() === normalizedName;
  });
  if (duplicate) {
    return `A sub-category named "${name.trim()}" already exists under this parent category`;
  }
  return undefined;
}

export function validateDuplicateFoodSubCategoryCode(
  code: string,
  parentCategoryCode: string,
  records: FoodSubCategoryRecord[],
  editingId?: string,
): string | undefined {
  const normalizedCode = code.trim().toUpperCase();
  const parent = parentCategoryCode.trim().toLowerCase();
  if (!normalizedCode || !parent) return undefined;
  const duplicate = records.some((row) => {
    if (editingId && row._id === editingId) return false;
    if (resolveFoodSubCategoryParentCode(row) !== parent) return false;
    return String(row.code ?? '').trim().toUpperCase() === normalizedCode;
  });
  if (duplicate) {
    return `Code "${code.trim()}" is already used by another sub-category under this parent category`;
  }
  return undefined;
}

/** Parent category master types — list/form labels show display name only (code has its own column). */
export const PARENT_CATEGORY_MASTER_TYPES = new Set<string>([
  'food_categories',
  'grocery_categories',
]);

/** True when a master ref should render as parent category name (not "Name (code)"). */
export function isParentCategoryRefField(
  masterTypeKey: string,
  fieldKey: string,
  refType: string | null,
): boolean {
  if (fieldKey === 'parentCategoryCode' || fieldKey === 'addonGroupCode') return true;
  return (
    (masterTypeKey === 'food_sub_categories' || masterTypeKey === 'grocery_sub_categories')
    && !!refType
    && PARENT_CATEGORY_MASTER_TYPES.has(refType)
  );
}

export function formatFoodParentCategoryLabel(name: string | undefined, code: string): string {
  const trimmedName = String(name ?? '').trim();
  const trimmedCode = String(code ?? '').trim();
  if (trimmedName && trimmedCode) return trimmedName;
  return trimmedName || trimmedCode || '—';
}

export function formatParentCategoryRefLabel(
  masterMaps: Map<string, Map<string, string>>,
  refType: string,
  code: string,
): string {
  const normalized = String(code).trim();
  const map = masterMaps.get(refType);
  const name = map?.get(normalized)
    ?? map?.get(normalized.toUpperCase())
    ?? map?.get(normalized.toLowerCase());
  return formatFoodParentCategoryLabel(name, normalized);
}

export function restaurantHasMainBranch(
  restaurantSlug: string,
  records: Array<{ _id?: string; metadata?: Record<string, unknown> }>,
  editingId?: string,
): boolean {
  if (!restaurantSlug.trim()) return false;
  return records.some((row) => {
    if (editingId && row._id === editingId) return false;
    const meta = row.metadata || {};
    return String(meta.restaurantSlug ?? '') === restaurantSlug
      && String(meta.branchType ?? '') === 'main';
  });
}

export function validateRestaurantBranchType(
  branchType: string,
  restaurantSlug: string,
  records: Array<{ _id?: string; metadata?: Record<string, unknown> }>,
  editingId?: string,
): string | undefined {
  if (branchType !== 'main' || !restaurantSlug.trim()) return undefined;
  if (restaurantHasMainBranch(restaurantSlug, records, editingId)) {
    return 'This restaurant already has a Main branch. Choose Sub Branch or pick another restaurant.';
  }
  return undefined;
}

/** Food addon form: group/price in Properties — image promoted to Basic Info; sort order and status elsewhere. */
export function enrichFoodAddonMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => (
      field.key === 'price'
        ? {
          ...field,
          type: 'currency' as const,
          min: field.min ?? 0,
          helperText: field.helperText || 'Enter amount with up to 2 decimal places (e.g. 40.00)',
        }
        : field
    ));
}

/** Unit master form: unit type in Properties — sort order and status live elsewhere. */
export function enrichFoodUnitMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => (
      field.key === 'unitType'
        ? {
          ...field,
          type: 'text' as const,
          placeholder: field.placeholder || 'e.g. count, weight, volume',
          helperText: field.helperText || 'Pick a common type or enter your own — e.g. count, weight, volume, portion',
          options: FOOD_UNIT_TYPE_SUGGESTIONS.map((value) => ({
            value,
            label: formatFoodUnitTypeLabel(value),
          })),
        }
        : field
    ));
}

const FOOD_AVAILABILITY_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function resolveFoodAvailabilityDays(row: {
  metadata?: Record<string, unknown>;
}): string[] {
  const meta = row.metadata || {};
  const daysOfWeek = parseCommaSeparatedDayValues(String(meta.daysOfWeek ?? ''));
  if (daysOfWeek.length) return daysOfWeek;
  const day = String(meta.day ?? '').trim().toLowerCase();
  return day ? [day] : [];
}

export function sortFoodAvailabilityScheduleRows<T extends {
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const orderA = resolveRecordSortOrder(a);
    const orderB = resolveRecordSortOrder(b);
    if (orderA !== orderB) {
      if (orderA === 0) return 1;
      if (orderB === 0) return -1;
      return orderA - orderB;
    }
    const daysA = resolveFoodAvailabilityDays(a);
    const daysB = resolveFoodAvailabilityDays(b);
    const idxA = FOOD_AVAILABILITY_DAY_ORDER.indexOf(daysA[0] as typeof FOOD_AVAILABILITY_DAY_ORDER[number]);
    const idxB = FOOD_AVAILABILITY_DAY_ORDER.indexOf(daysB[0] as typeof FOOD_AVAILABILITY_DAY_ORDER[number]);
    if (idxA !== idxB) {
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    }
    const startA = String(a.metadata?.startTime ?? '');
    const startB = String(b.metadata?.startTime ?? '');
    if (startA !== startB) return startA.localeCompare(startB);
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

export function validateFoodAvailabilityScheduleName(
  name: string,
  daysOfWeek: string,
): string | undefined {
  const label = String(name ?? '').trim().toLowerCase();
  if (!label.includes('weekend')) return undefined;
  const days = parseCommaSeparatedDayValues(daysOfWeek);
  if (!days.includes('sat') || !days.includes('sun')) {
    return 'Schedules named "Weekend" must include both Saturday and Sunday, or rename to specify the day (e.g. Saturday All Day)';
  }
  return undefined;
}

/** Food availability form: multi-day + time window — sort order and status live elsewhere. */
export function enrichFoodAvailabilityScheduleMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  const hasDaysOfWeek = fields.some((field) => field.key === 'daysOfWeek');
  const legacyDayIndex = fields.findIndex((field) => field.key === 'day');
  const legacyDayField = legacyDayIndex >= 0 ? fields[legacyDayIndex] : undefined;
  const daysOfWeekField: MasterFieldDef = {
    ...(legacyDayField || {
      key: 'daysOfWeek',
      label: 'Days',
      type: 'select',
    }),
    key: 'daysOfWeek',
    label: 'Days',
    type: 'select',
    multiple: true,
    required: true,
    options: legacyDayField?.options?.length
      ? legacyDayField.options
      : TRANSPORT_DAY_OF_WEEK_OPTIONS.map((option) => ({ ...option })),
  };
  const sourceFields = hasDaysOfWeek
    ? fields
    : legacyDayField
      ? [
        ...fields.slice(0, legacyDayIndex),
        daysOfWeekField,
        ...fields.slice(legacyDayIndex + 1),
      ]
      : [
        daysOfWeekField,
        ...fields,
      ];

  return sourceFields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
      && field.key !== 'day'
    ))
    .map((field) => {
      if (field.key === 'daysOfWeek') {
        return {
          ...field,
          label: 'Days',
          type: 'select' as const,
          multiple: true,
          required: true,
          helperText: 'Select one or more days — e.g. Mon, Tue, Wed or Sat, Sun for weekend',
          options: TRANSPORT_DAY_OF_WEEK_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'startTime' || field.key === 'endTime') {
        return {
          ...field,
          type: 'time' as const,
          required: true,
          helperText: '24-hour format HH:mm — use the time picker',
          pattern: '^([01]\\d|2[0-3]):[0-5]\\d$',
          patternMessage: 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)',
        };
      }
      return field;
    });
}

/** Kitchen station form: station type in Properties — sort order and status live elsewhere. */
export function enrichKitchenStationMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => (
      field.key === 'stationType'
        ? {
          ...field,
          type: 'text' as const,
          placeholder: field.placeholder || 'e.g. hot_kitchen, bakery, grill',
          helperText: field.helperText || 'Pick a common type or enter your own — e.g. Hot Kitchen, Bakery, Grill',
          options: KITCHEN_STATION_TYPE_SUGGESTIONS.map((value) => ({
            value,
            label: formatKitchenStationTypeLabel(value),
          })),
        }
        : field
    ));
}

/** Preparation time form: minutes only in Properties — sort order and status live elsewhere. */
export function enrichPreparationTimeMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => (
      field.key === 'minutes'
        ? {
          ...field,
          helperText: field.helperText || 'Whole minutes from 1 to 180 — display name becomes e.g. 15 mins',
        }
        : field
    ));
}

/** Addon group form: min/max/required in Properties — sort order and status live elsewhere. */
export function enrichAddonGroupMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => (
    field.key !== 'status'
    && field.key !== 'sortOrder'
  ));
}

/** Food image master form: food code / type / image — sort order and status live elsewhere. */
export function enrichFoodImageMasterMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
    ))
    .map((field) => {
      if (field.key === 'imageUrl' || field.type === 'image') {
        return {
          ...field,
          type: 'image' as const,
          required: true,
          imageMaxWidth: FOOD_IMAGE_MASTER_MAX_WIDTH,
          imageMaxHeight: FOOD_IMAGE_MASTER_MAX_HEIGHT,
          helperText: `PNG, JPEG, or WebP · max ${FOOD_IMAGE_MASTER_MAX_WIDTH}×${FOOD_IMAGE_MASTER_MAX_HEIGHT}px`,
        };
      }
      return field;
    });
}

/** Food tag form: color only in Properties — sort order and status live elsewhere. */
export function enrichFoodTagMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
      && field.key !== 'description'
      && field.key !== 'inputType'
    ))
    .map((field) => (
      field.key === 'color'
        ? {
          ...field,
          type: 'color' as const,
          placeholder: '#f59e0b',
          helperText: 'Pick a colour or enter a hex code — e.g. #f59e0b',
        }
        : field
    ));
}

/** Food variant form: unique code lives in Basic Info — hide duplicate Food Item Code and status. */
export function enrichFoodVariantMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'foodCode'
    ))
    .map((field) => {
      if (field.key === 'price' || field.key === 'costPrice') {
        return {
          ...field,
          type: 'currency' as const,
          min: field.min ?? 0,
          helperText: field.helperText || 'Enter amount with up to 2 decimal places (e.g. 199.50)',
        };
      }
      return field;
    });
}

/** Food attribute value form: parent attribute in Basic Info — hide duplicate metadata fields. */
export function enrichFoodAttributeValueMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => (
    field.key !== 'status'
    && field.key !== 'sortOrder'
    && field.key !== 'description'
    && field.key !== 'inputType'
    && field.key !== 'attributeCode'
  ));
}

/** Food attribute form: input type only in Properties — sort order and status live elsewhere. */
export function enrichFoodAttributeMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => (
    field.key !== 'status'
    && field.key !== 'sortOrder'
    && field.key !== 'description'
  ));
}
